export interface RequestConfig {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  cache?: RequestCache;
  credentials?: RequestCredentials;
  params?: Record<string, any>;  // Added for query parameters
}

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  headers: Headers;
}

export interface ApiError {
  status?: number;
  message: string;
  data?: any;
}

class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor() {
    this.baseURL = process.env.NODE_ENV === 'production'
      ? 'https://hyperlocal.cdmsuite.com'
      : 'http://localhost:8787';

    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Cache-Control': 'public, max-age=3600',
    };
  }

  private buildUrl(endpoint: string, params?: Record<string, any>): string {
    const url = new URL(`${this.baseURL}${endpoint}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    return url.toString();
  }

  private async request<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        ...this.defaultHeaders,
        ...config.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const url = this.buildUrl(endpoint, config.params);
      const { params, ...fetchConfig } = config;  // Remove params from fetch config

      const response = await fetch(url, {
        ...fetchConfig,
        headers,
        mode: 'cors',
        credentials: 'include',
      });

      // Handle Cloudflare-specific error codes
      if (response.status === 521) {
        throw new Error('The origin server is down. Please try again later.');
      } else if (response.status === 522) {
        throw new Error('Connection to server timed out. Please try again.');
      } else if (response.status === 524) {
        throw new Error('Request timeout. Please try again.');
      }

      // Handle authentication errors
      if (response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        throw new Error('Authentication required');
      }

      // Handle rate limiting
      if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      }

      // Handle CORS errors
      if (response.status === 0) {
        throw new Error('CORS error: Unable to access the API. Please check the server configuration.');
      }

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Cache successful GET responses
      if (config.method === 'GET' && response.ok && 'caches' in window) {
        try {
          const cache = await caches.open('api-cache');
          await cache.put(url, new Response(JSON.stringify(data), response));
        } catch (error) {
          console.warn('Cache write failed:', error);
        }
      }

      if (!response.ok) {
        throw {
          status: response.status,
          message: data.error || 'An error occurred',
          data,
        };
      }

      return {
        data,
        status: response.status,
        headers: response.headers,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw {
          message: error.message,
          status: error instanceof Response ? error.status : undefined,
        } as ApiError;
      }
      throw error;
    }
  }

  async get<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    // Try cache first if it's a GET request
    if ('caches' in window) {
      try {
        const url = this.buildUrl(endpoint, config.params);
        const cache = await caches.open('api-cache');
        const cachedResponse = await cache.match(url);
        if (cachedResponse) {
          const data = await cachedResponse.json();
          return {
            data,
            status: 200,
            headers: cachedResponse.headers,
          };
        }
      } catch (error) {
        console.warn('Cache access failed:', error);
      }
    }

    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data?: any, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data?: any, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'DELETE',
    });
  }
}

const client = new ApiClient();
export default client;
